import { Entitlements } from '@experiences/constants';
import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import {
    UiStack,
    UiSuspensefulOutlet,
} from '@experiences/ui-common';
import { ApProgressSpinner } from '@uipath/portal-shell-react';
import type { PropsWithChildren } from 'react';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Navigate } from 'react-router';

import { routePaths } from '../../common/constants/routePaths';
import { useOrganizationName } from '../../common/hooks/useOrganizationName';
import BreadcrumbProvider from '../../common/providers/BreadcrumbProvider';
import CheckGuard from '../../container/helpers/CheckGuard';
import type { UiRouteObject } from '../../container/routeConfigs/UiRouteObject';
import { useEntitlements } from '../entitlements/EntitlementsContextProvider';
import { UpgradeGuard } from '../upgrade/UpgradeGuard';
import GovernanceWizardComponent from './subcomponents/GovernanceWizardComponent';

const GovernancePageComponent = React.lazy(() => import('./GovernancePageComponent'));
const GovernancePoliciesTab = React.lazy(() => import('./GovernancePoliciesTab'));
const GovernanceTracesTab = React.lazy(() => import('./GovernanceTracesTab'));
const PolicyResourceRule = React.lazy(() => import('./subcomponents/steps/policy-definition/rules/resource/PolicyResourceRule'));
// eslint-disable-next-line max-len
const PolicyAutomationRule = React.lazy(() => import('./subcomponents/steps/policy-definition/rules/automation/PolicyAutomationRule'));
// eslint-disable-next-line max-len
const PolicyAccessDecisionRule = React.lazy(() => import('./subcomponents/steps/policy-definition/rules/access-decision/PolicyAccessDecisionRule'));

// ─── Org-level provider ──────────────────────────────────────────────────────

const GovernanceProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const { formatMessage: translate } = useIntl();

    const EnableDataAccessPoliciesGovernance = useFeatureFlagValue(Features.EnableDataAccessPoliciesGovernance.name);

    const {
        hasEntitlement, isLoading: isLoadingEntitlements,
    } = useEntitlements();
    const isSapOrganization = hasEntitlement(Entitlements.PlatformSapEnabled);

    const organizationName = useOrganizationName();

    const breadcrumbs = useMemo(() => [
        {
            index: 0,
            link: routePaths.admin,
            name: organizationName,
        },
        {
            index: 1,
            link: routePaths.adminGovernance,
            name: translate({ id: 'CLIENT_GOVERNANCE' }),
        },
        {
            index: 2,
            link: routePaths.adminGovernanceCreate,
            name: translate({ id: 'CLIENT_GOVERNANCE_ADD_POLICY' }),
        },
        {
            index: 2,
            link: routePaths.adminGovernanceCreatePolicyResources,
            name: translate({ id: 'CLIENT_GOVERNANCE_ADD_POLICY' }),
        },
        {
            index: 2,
            link: routePaths.adminGovernanceCreatePolicyAutomations,
            name: translate({ id: 'CLIENT_GOVERNANCE_ADD_POLICY' }),
        },
        {
            index: 2,
            link: routePaths.adminGovernanceCreatePolicyAccessDecision,
            name: translate({ id: 'CLIENT_GOVERNANCE_ADD_POLICY' }),
        },
        {
            index: 2,
            link: routePaths.adminGovernanceEdit,
            name: translate({ id: 'CLIENT_GOVERNANCE_EDIT_POLICY' }),
        },
        {
            index: 2,
            link: routePaths.adminGovernanceEditPolicyResources,
            name: translate({ id: 'CLIENT_GOVERNANCE_EDIT_POLICY' }),
        },
        {
            index: 2,
            link: routePaths.adminGovernanceEditPolicyAutomations,
            name: translate({ id: 'CLIENT_GOVERNANCE_EDIT_POLICY' }),
        },
        {
            index: 2,
            link: routePaths.adminGovernanceEditPolicyAccessDecision,
            name: translate({ id: 'CLIENT_GOVERNANCE_EDIT_POLICY' }),
        },
    ], [ organizationName, translate ]);

    if (isLoadingEntitlements) {
        return <BreadcrumbProvider breadcrumbs={breadcrumbs}>
            <UiStack
                align="center"
                justify="center"
                grow={1}
                data-cy="governance-entitlements-loading">
                <ApProgressSpinner />
            </UiStack>
        </BreadcrumbProvider>;
    }

    return <BreadcrumbProvider breadcrumbs={breadcrumbs}>
        {CheckGuard(EnableDataAccessPoliciesGovernance && !isSapOrganization, <>
            {children}
        </>)}
    </BreadcrumbProvider>;
};

// ─── Org-level routes ────────────────────────────────────────────────────────

export const GovernanceRoutes: UiRouteObject = {
    path: routePaths.adminGovernance,
    element: <GovernanceProvider>
        <UiSuspensefulOutlet />
    </GovernanceProvider>,
    children: [
        {
            element: <UpgradeGuard requiredEntitlement={Entitlements.Governance}>
                <GovernancePageComponent />
            </UpgradeGuard>,
            children: [
                {
                    index: true,
                    element: <Navigate
                        to="policies"
                        replace />,
                },
                {
                    path: routePaths.adminGovernancePolicies,
                    element: <GovernancePoliciesTab />,
                },
                {
                    path: routePaths.adminGovernanceTraces,
                    element: <GovernanceTracesTab />,
                },
            ],
        },
        {
            path: routePaths.adminGovernanceEdit,
            element: <UpgradeGuard requiredEntitlement={Entitlements.Governance}>
                <GovernanceWizardComponent mode="edit" />
            </UpgradeGuard>,
            children: [
                {
                    path: routePaths.adminGovernanceEditPolicyResources,
                    element: <PolicyResourceRule />,
                },
                {
                    path: routePaths.adminGovernanceEditPolicyAutomations,
                    element: <PolicyAutomationRule />,
                },
                {
                    path: routePaths.adminGovernanceEditPolicyAccessDecision,
                    element: <PolicyAccessDecisionRule />,
                },
            ],
        },
        {
            path: routePaths.adminGovernanceCreate,
            element: <UpgradeGuard requiredEntitlement={Entitlements.Governance}>
                <GovernanceWizardComponent mode="create" />
            </UpgradeGuard>,
            children: [
                {
                    path: routePaths.adminGovernanceCreatePolicyResources,
                    element: <PolicyResourceRule />,
                },
                {
                    path: routePaths.adminGovernanceCreatePolicyAutomations,
                    element: <PolicyAutomationRule />,
                },
                {
                    path: routePaths.adminGovernanceCreatePolicyAccessDecision,
                    element: <PolicyAccessDecisionRule />,
                },
            ],
        },
    ],
};
